import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/plan")({
  component: Plan,
  head: () => ({
    meta: [
      { title: "Project Plan · Buddha KA · KAfrequency" },
      { name: "description", content: "Internal planning document for the Buddha KA + KAfrequency digital ecosystem." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-14 font-serif text-3xl text-gold-luminous sm:text-4xl">{children}</h2>;
}
function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="mt-8 font-serif text-xl text-gold">{children}</h3>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-3 text-muted-foreground">{children}</p>;
}
function List({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-1.5 text-muted-foreground">
      {items.map((i) => <li key={i} className="pl-4 -indent-4">— {i}</li>)}
    </ul>
  );
}

function Plan() {
  return (
    <article className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Internal Planning Document</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl">Buddha KA · KAfrequency — Build Plan</h1>
      <p className="mt-6 text-muted-foreground">
        The reference document for design, structure, and phased delivery. Kept in-project so intent stays alongside implementation.
      </p>

      <H>1 · PRD Summary</H>
      <P>
        A cinematic, mobile-first digital temple: futuristic Kemetic sacred aesthetic uniting music,
        activations, teachings, visual art, books, community, and future commerce. Not a template.
        A world users <em>enter</em>. Every unknown fact stays a labeled placeholder — nothing is fabricated.
      </P>

      <H>2 · Sitemap</H>
      <List items={[
        "/  Home",
        "/music  · /music/$slug",
        "/activations  · /activations/$slug",
        "/wisdom  · /wisdom/$slug",
        "/visuals · /books · /books/$slug · /shop · /shop/$slug",
        "/community · /about · /press · /contact · /search",
        "/account · /admin",
        "Legal: /privacy · /terms · /refund · /shipping · /accessibility",
        "System: /newsletter/confirm · /thank-you · 404",
      ]} />

      <H>3 · Primary Visitor Journeys</H>
      <List items={[
        "Awakening seeker → homepage → Daily Activation → Night Seal → newsletter",
        "Music discoverer → Featured Release → Release detail → streaming platform → community",
        "Reader → Wisdom archive → Article → related Activation → newsletter",
        "Booker / partner → Contact → Booking inquiry with budget & timeline",
        "Member → Community Gateway → tier selection (Phase 2 auth + payments)",
      ]} />

      <H>4 · Database Plan (Phase 2 — Lovable Cloud)</H>
      <P>
        All user-owned tables enable RLS. Roles live in a separate <code>user_roles</code> table with
        a <code>has_role</code> security-definer function. All public tables require explicit GRANTs.
      </P>
      <List items={[
        "auth: users, profiles, user_roles",
        "catalog: releases, tracks, track_credits, lyrics, platforms, release_links, playlists",
        "practice: activations, activation_steps, saved_activations",
        "editorial: wisdom_posts, categories, tags",
        "publishing: books, book_formats",
        "gallery: visuals",
        "commerce: products, product_variants, orders, order_items",
        "membership: memberships, subscriptions",
        "email: email_subscribers, subscriber_interests",
        "ops: contact_submissions, press_assets, announcements",
        "system: site_settings, navigation_items, homepage_sections, analytics_events",
        "All tables: slugs, timestamps, publication states, SEO fields, audit columns.",
      ]} />

      <H>5 · Component Inventory</H>
      <List items={[
        "Navigation, MobileMenu, Footer, AnkhMark",
        "HeroPortal, SectionHeader, PlaceholderArt",
        "ReleaseCard, AlbumCard, TrackList, AudioPlayerUI, PlatformButton",
        "ActivationCard, BreathTimer (functional)",
        "ArticleCard, QuoteBlock",
        "VisualCard, BookCard, ProductCard, CommunityTierCard",
        "NewsletterForm, ContactForm, ShareMenu, SearchModal, MediaLightbox",
        "Empty / Loading / Error states; Admin Table, Editor, ConfirmationModal (Phase 2)",
      ]} />

      <H>6 · Design System</H>
      <List items={[
        "Palette (oklch): obsidian, charcoal, temple gold, luminous gold, ivory, emerald, deep violet",
        "Typography: Cormorant Garamond serif + Inter Tight sans",
        "Tokens: --gold, --gold-luminous, --ivory, --emerald, --violet, --gradient-halo, --gradient-gold, --shadow-gold, --shadow-halo",
        "Motion: halo pulse, frequency rings, rise-in reveals — all reduced-motion safe",
        "Never hardcode hex in components — always semantic tokens",
      ]} />

      <H>7 · Phased Implementation</H>
      <H3>Phase 1 (shipped now)</H3>
      <List items={[
        "Design system, navigation, mobile menu, footer, sitemap, robots",
        "Homepage with all 13 sections",
        "/music archive + release detail template",
        "/activations archive + detail with functional breath timer",
        "/wisdom archive + article template",
        "/about + /contact (client validation, multi-inquiry-type)",
        "Stubs for future routes with correct head metadata",
      ]} />
      <H3>Phase 2</H3>
      <List items={[
        "Enable Lovable Cloud; provision full schema with RLS + roles",
        "Auth + accounts + Admin dashboard",
        "Server-side validated forms + Resend confirmations + rate limits",
        "Persistent cross-route audio player",
        "Visuals archive + press center + global search",
      ]} />
      <H3>Phase 3</H3>
      <List items={[
        "Stripe test mode via server functions; digital delivery",
        "Membership tiers (Listener / Frequency Member / Inner Circle) — pricing TBD",
        "Books commerce, product catalog",
        "Analytics events + JSON-LD across all content types",
      ]} />

      <H>8 · Required Assets & Information</H>
      <List items={[
        "Official Buddha KA portrait (young Kemetic Buddha, obsidian streetwear, gold ankh, dreadlocks, subtle halo)",
        "Cover art for all five releases",
        "Book covers × 3",
        "Confirmed release years, tracklists, credits, lyric selections",
        "Streaming platform URLs (Spotify, Apple Music, YouTube, SoundCloud, etc.)",
        "Social handles (never fabricated)",
        "Book formats, preview links, purchase pathways",
        "Membership tier pricing (when decided)",
        "Media kit assets + press contact",
      ]} />

      <H>9 · Critical Technical Decisions</H>
      <List items={[
        "TanStack Start + file-based routing; SSR-friendly per-route head metadata",
        "Tailwind v4 CSS-first design tokens; shadcn variants; no hardcoded colors",
        "Fonts loaded via <link> in root head (never @import remote URL)",
        "Breath timer uses requestAnimationFrame; honors prefers-reduced-motion",
        "No Lovable Cloud in Phase 1 (presentation only); enabled at Phase 2 kickoff",
        "sitemap.xml generated at /sitemap.xml server route; robots.txt static",
      ]} />

      <H>10 · Risk & Quality Checklist</H>
      <List items={[
        "Mobile: 320 / 375 / 430 / 768 / 1024 / 1440+ — no clipping, no horizontal scroll",
        "Tap targets ≥ 44px on primary CTAs; grid pattern for text + widget rows",
        "Semantic HTML, one <main> per route, visible focus rings, alt text on every image slot",
        "Reduced motion respected; no auto-playing audio",
        "No fabricated dates, prices, streaming URLs, quotes, statistics",
        "SEO: unique title/description/og per route; canonical self-references; sitemap current",
        "Security (Phase 2): auth flow, RLS on all user-owned tables, protected admin, server validation, sanitization, rate limits, safe file uploads, no frontend secrets",
      ]} />

      <div className="mt-16 rounded-2xl border hairline bg-charcoal p-8">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Guiding Principle</p>
        <p className="mt-3 font-serif text-2xl italic text-ivory">
          &ldquo;The final experience must feel like a world people enter — not merely a website they visit.&rdquo;
        </p>
      </div>

      <Link to="/" className="mt-12 inline-flex text-sm text-gold hover:text-gold-luminous">← Return to the field</Link>
    </article>
  );
}
